import dayjs, { Dayjs } from "dayjs";

const DATE_FORMAT = "YYYY-MM-DD";

// Be Aware > Conversions Upload review window.
// Range: today - 60 days to today - 2 days.
// Last 2 days are excluded since ad platforms aren't real-time.
// Used for both the API payload and the filter date range.

export const CONVERSIONS_UPLOAD_LOOKBACK_DAYS = 60;
export const CONVERSIONS_UPLOAD_EXCLUDE_DAYS = 2;
export function getConversionsUploadDateRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  return {
    start: today.subtract(CONVERSIONS_UPLOAD_LOOKBACK_DAYS, "day"),
    end: today.subtract(CONVERSIONS_UPLOAD_EXCLUDE_DAYS, "day"),
  };
}

// Be Aware > Fraud Players review window: the current month (MTD).
export function getFraudPlayersDateRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  return {
    start: today.startOf("month"),
    end: today.endOf("month"),
  };
}

// Be Aware > Negative Balance review window: the previous full month.
export function getNegativeBalanceDateRange(): { start: Dayjs; end: Dayjs } {
  const previousMonth = dayjs().subtract(1, "month");
  return {
    start: previousMonth.startOf("month"),
    end: previousMonth.endOf("month"),
  };
}

// Anomalies > Missing Data review window: the last 2 days.
export function getMissingDataDateRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  return {
    start: today.subtract(2, "day"),
    end: today,
  };
}

// Anomalies > FTD > 5+ Clicks with 0 FTD review window: the last 7 days.
export function getClicksWithFtdDateRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  return {
    start: today.subtract(7, "day"),
    end: today,
  };
}

// Anomalies > FTD > Problematic FTDs review window: the last 60 days.
export function getProblematicFtdDateRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  return {
    start: today.subtract(60, "day"),
    end: today,
  };
}

// Anomalies > Income review window: month-to-date (start of the current
// month through today).
export function getIncomeMTDDateRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  return {
    start: today.startOf("month"),
    end: today,
  };
}

// Anomalies > Brands review window: the last 60 days (today-59 to today,
// a true 60-day inclusive window). Matches the desktop hook's
// fetchAnomaliesBrandData, which sends this exact range to the
// No Player Report query.
export function getBrandsDateRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  return {
    start: today.subtract(59, "day"),
    end: today,
  };
}

export function getDateRangeForTimeframe(timeframe: string): {
  startDate: string;
  endDate: string;
} {
  const today = dayjs();

  switch (timeframe) {
    case "two_days_ago":
      return {
        startDate: today.subtract(2, "day").format(DATE_FORMAT),
        endDate: today.subtract(2, "day").format(DATE_FORMAT),
      };

    case "three_days_ago":
      return {
        startDate: today.subtract(3, "day").format(DATE_FORMAT),
        endDate: today.subtract(3, "day").format(DATE_FORMAT),
      };

    case "mtd":
      return {
        startDate: today.startOf("month").format(DATE_FORMAT),
        endDate: today.format(DATE_FORMAT),
      };

    case "yesterday":
    default:
      return {
        startDate: today.subtract(1, "day").format(DATE_FORMAT),
        endDate: today.subtract(1, "day").format(DATE_FORMAT),
      };
  }
}

// ─── Table constants ────────────────────────────────────────────────────────

export const PERC_SUFFIX = "_perc";

// Columns that should only appear when timeframe is MTD
export const MTD_ONLY_KEYS = new Set([
  "ftd_incent_perc",
  "perc_incent_tier5",
  "qftd_incent",
  "epc_rs",
]);

export const NO_COMPARISON_KEYS = new Set([
  "brand_display_name",
  "brand_id",
  "geo",
]);

// Performance table cells that open the Deep View drawer on click
export const DEEP_VIEW_KEYS = new Set([
  "clicks",
  "reg",
  "ftd",
  "qftd",
  "cpa_income_org_currency",
  "revshare_income_org_currency",
  "totalincome_org_currency",
  "total_income_usd",
  "netrev",
]);

// Frontend-computed percentage for columns where _perc is not provided by API
export const COMPUTED_PERC: Record<
  string,
  (row: Record<string, any>) => number | null
> = {};

// ─── Badge styles ────────────────────────────────────────────────────────────

export const RED = { bg: "#FDEDED", color: "#D32F2F", border: "#D32F2F80" };
export const YELLOW = { bg: "#FFFAEB", color: "#DC6803", border: "#ffc593" };
export const GREEN = { bg: "#EDF7ED", color: "#2E7D32", border: "#2E7D3280" };

export const getBadgeStyle = (
  percKey: string,
  percent: number,
  isMtd: boolean
) => {
  // QFTD — MTD only: high is bad (inverted scale)
  if (percKey === "qftd_perc" && isMtd) {
    if (percent >= 20) return RED;
    if (percent > 10) return YELLOW;
    return GREEN;
  }
  // Incent & Tier 5 — MTD only: two-colour scale
  if (percKey === "perc_incent_tier5" && isMtd) {
    return percent >= 25 ? RED : GREEN;
  }
  // FTD and all other _perc fields (including QFTD non-MTD)
  // Spec: ≤12 red | 12<x<20 yellow | ≥20 green
  if (percent <= 12) return RED;
  if (percent < 20) return YELLOW;
  return GREEN;
};

// ─── Currency ────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS: [string, string][] = [
  ["_usd", "$"],
  ["_gbp", "£"],
  ["_eur", "€"],
  ["_brl", "R$"],
  ["_cad", "C$"],
  ["_ron", "lei"],
  ["_ils", "₪"],
];

// Supports both ISO codes ("GBP") and full names ("Pound sterling") from deal_currency
const CURRENCY_NAME_SYMBOLS: Record<string, string> = {
  // ISO codes
  USD: "$",
  GBP: "£",
  EUR: "€",
  BRL: "R$",
  CAD: "C$",
  RON: "lei",
  ILS: "₪",
  // Full names as returned by the API
  DOLLAR: "$",
  "POUND STERLING": "£",
  EURO: "€",
  "BRAZILIAN REAL": "R$",
  "CANADIAN DOLLAR": "C$",
  "ROMANIAN LEU": "lei",
  "ISRAELI NEW SHEKEL": "₪",
  "NEW ISRAELI SHEKEL": "₪",
};

export const getCurrencySymbolFromCode = (
  code: string | null | undefined
): string | null => {
  if (!code) return null;
  return CURRENCY_NAME_SYMBOLS[String(code).toUpperCase()] ?? null;
};

// Income fields denominated in the deal's org currency — symbol derived from deal_currency row value
export const ORG_CURRENCY_INCOME_KEYS = new Set([
  "cpa_income_org_currency",
  "revshare_income_org_currency",
  "totalincome_org_currency",
]);

// NOTE: Custom overrides added to return "$" symbol for EPC, EPC CPA, Net Rev, PV, CPA PV,
// and RS Income fields, as these requirements are not specified in the default schema documentation.
export const getCurrencySymbol = (key: string): string | null => {
  const lower = key.toLowerCase();

  // Override to return "$" for EPC, EPC CPA, Net Rev, PV, CPA PV, and RS Income fields in Biz Dev Dashboard
  const dollarKeys = new Set([
    "epc",
    "epc_cpa",
    "net_rev",
    "cpa_pv",
    "epc_rs",
    "pv",
    "non_negative_rs_income",
    "rs_income",
  ]);

  if (dollarKeys.has(lower)) {
    return "$";
  }

  for (const [substr, symbol] of CURRENCY_SYMBOLS) {
    if (lower.includes(substr)) return symbol;
  }
  return null;
};

// ─── Formatting ──────────────────────────────────────────────────────────────

export const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "N/A";
  const num = typeof value === "number" ? value : Number(String(value));
  if (!isNaN(num)) {
    if (Number.isInteger(num)) {
      return Math.round(num).toLocaleString("en-US");
    }
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return String(value);
};

// ─── Column building ─────────────────────────────────────────────────────────

export interface OutputMetaField {
  title: string;
  type: string;
  description: string;
  order: string | number;
}

export interface TableColumn {
  key: string;
  title: string;
  order: number;
  hasPerc: boolean;
  isPercentageValue?: boolean;
  tooltip?: string;
}

// Keys whose _perc variant is displayed as a standalone column (not merged into the value column)
const SPLIT_PERC_COLUMNS = new Set(["ftd_incent", "qftd_incent"]);

// Custom label used inside the parentheses for merged _perc columns, e.g. "FTD (%CVR)" instead of "FTD (%FTD)"
const PERC_LABEL_OVERRIDES: Record<string, string> = {
  ftd: "CVR",
  qftd: "CVR",
};

// Tooltip text shown on hover of the column header info icon (placeholder — fill in final copy later)
export const COLUMN_TOOLTIPS: Record<string, string> = {
  ftd: "The percentage of FTD's of Click Outs",
  qftd: "The percentage of QFTD's of Click Outs",
  ftd_incent_perc: "The percentage of Incentivised traffic of FTD's",
  perc_incent_tier5:
    "The percentage of Incentivised traffic & Tier 5 players of FTD's",
};

// Keys whose raw API value is already in 0–100 percentage form (show badge, no ×100)
export const NATIVE_PERCENTAGE_KEYS = new Set<string>([]);

// Keys whose raw API value is a 0–1 decimal that should be rendered as a badge (×100)
// Includes both: NATIVE_PERCENTAGE_KEYS (no multiply) and decimal perc keys (multiply)
const PERCENTAGE_VALUE_KEYS = new Set(["ftd_incent_perc", "perc_incent_tier5"]);

// Keys whose values should always be rounded to whole numbers (e.g. org-currency income fields)
export const ROUND_KEYS = new Set([
  "cpa_income_org_currency",
  "revshare_income_org_currency",
  "totalincome_org_currency",
  "netrev",
  "cpa_pv_qftd",
]);

// Columns hidden until the data is available from the backend
// qftd_incent_perc is hidden — its role is covered by the standalone ftd_incent_perc (%Incent) column
export const HIDDEN_KEYS = new Set([
  "total_income",
  "qftd_incent_perc",
  "ftd_incent",
]);

// Figma-specified columns in the order they should appear at the start of the table.
// Any backend column not in this list will appear after these, sorted by its backend order.
export const COLUMN_TITLE_OVERRIDES: Record<string, string> = {
  // identity fields
  brand_id: "Brand ID",
  brand_display_name: "Brand Name",
  // traffic
  clicks: "Click Out",
  reg: "Regs",
  // conversions (ftd/qftd get (%X) appended automatically via hasPerc logic)
  ftd: "FTD",
  qftd: "QFTD",
  // income
  cpa_income_org_currency: "CPA Income",
  revshare_income_org_currency: "RS Income",
  total_income_usd: "Total Income $",
  totalincome_org_currency: "Total Income",
  // performance
  roi: "ROI",
  netrev: "NetRev",
  net_revenue_usd: "Net Revenue $",
  epc: "EPC",
  pv: "PV",
  epc_cpa: "EPC CPA",
  cpa_pv: "CPA PV",
  cpa_pv_qftd: "CPA PV QFTD",
  epc_rs: "EPC RS",
  non_negative_rs_income: "Non Negative RS Income",
  // MTD-only
  qftd_incent: "QFTD INCENT",
  ftd_incent_perc: "%INCENT",
  perc_incent_tier5: "INCENT & TIER 5",
};

export const buildColumns = (
  outputMetadata: Record<string, OutputMetaField>,
  isMtd = false
): TableColumn[] => {
  const metaKeys = Object.keys(outputMetadata);

  // _perc keys that should NOT be merged — they appear as standalone columns
  const percKeys = new Set(
    metaKeys.filter((k) => {
      if (!k.endsWith(PERC_SUFFIX)) return false;
      const base = k.slice(0, -PERC_SUFFIX.length);
      return !SPLIT_PERC_COLUMNS.has(base);
    })
  );

  const columns = metaKeys
    .filter((key) => !percKeys.has(key))
    .filter((key) => isMtd || !MTD_ONLY_KEYS.has(key))
    .filter((key) => !HIDDEN_KEYS.has(key))
    .filter((key) => outputMetadata[key].order !== "" || MTD_ONLY_KEYS.has(key))
    .map((key) => {
      const hasPerc = percKeys.has(key + PERC_SUFFIX);
      const isPercentageValue = PERCENTAGE_VALUE_KEYS.has(key);
      const rawTitle =
        COLUMN_TITLE_OVERRIDES[key] ??
        outputMetadata[key].title.replace(/_/g, " ");
      const percLabel = PERC_LABEL_OVERRIDES[key] ?? rawTitle;
      const title = hasPerc ? `${rawTitle} (%${percLabel})` : rawTitle;
      const tooltip = COLUMN_TOOLTIPS[key];
      return {
        key,
        title,
        order: Number(outputMetadata[key].order),
        hasPerc,
        isPercentageValue,
        ...(tooltip !== undefined && { tooltip }),
      };
    });

  const MTD_ORDER = ["qftd_incent", "ftd_incent_perc", "perc_incent_tier5"];
  const mtdColumns = MTD_ORDER.flatMap((k) =>
    columns.filter((c) => c.key === k)
  );
  const rest = columns
    .filter((c) => !MTD_ONLY_KEYS.has(c.key) || c.key === "epc_rs")
    .filter((c) => !MTD_ORDER.includes(c.key))
    .sort((a, b) => a.order - b.order);

  const qftdIndex = rest.findIndex((c) => c.key === "qftd");
  const insertAt = qftdIndex >= 0 ? qftdIndex + 1 : rest.length;
  rest.splice(insertAt, 0, ...mtdColumns);
  return rest;
};

// ─── Sorting ─────────────────────────────────────────────────────────────────

export const sortRows = (
  rows: Record<string, any>[],
  sortKey: string,
  sortDir: "asc" | "desc"
): Record<string, any>[] => {
  if (!sortKey) return rows;
  return [...rows].sort((a, b) => {
    const aVal = a[sortKey] ?? "";
    const bVal = b[sortKey] ?? "";
    if (aVal === bVal) return 0;
    const cmp = aVal < bVal ? -1 : 1;
    return sortDir === "asc" ? cmp : -cmp;
  });
};

// ─── Diff calculation ────────────────────────────────────────────────────────

export const calcDiffPercent = (primary: any, comp: any): number | null => {
  const a = parseFloat(String(primary ?? "").replace(/[, ]/g, ""));
  const b = parseFloat(String(comp ?? "").replace(/[, ]/g, ""));
  if (isNaN(a) || isNaN(b) || b === 0) return null;
  return ((a - b) / Math.abs(b)) * 100;
};

// ─── CSV Export ───────────────────────────────────────────────────────────────

const escapeCsvCell = (value: string): string => {
  // Wrap in quotes if the value contains a comma, quote, or newline
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export interface ExportOptions {
  columns: TableColumn[];
  primaryRows: Record<string, any>[];
  comparisonRows: Record<string, any>[];
  primaryLabel: string;
  comparisonLabel: string;
  isMtd: boolean;
  filename?: string;
}

export const exportToCSV = ({
  columns,
  primaryRows,
  comparisonRows,
  primaryLabel,
  comparisonLabel,
  filename = "performance_export",
}: ExportOptions): void => {
  const hasComparison = comparisonRows.length > 0;

  // ── Build header row ──────────────────────────────────────────────────────
  const headers: string[] = [];

  columns.forEach((col) => {
    const baseTitle = col.title
      // Strip the " (%TITLE)" suffix that buildColumns appends for perc columns
      .replace(/\s+\(%[^)]+\)$/, "")
      .trim();

    headers.push(`${baseTitle} [${primaryLabel}]`);

    if (col.hasPerc) {
      headers.push(`${baseTitle} % [${primaryLabel}]`);
    }

    if (hasComparison && !NO_COMPARISON_KEYS.has(col.key)) {
      headers.push(`${baseTitle} [${comparisonLabel}]`);

      if (col.hasPerc) {
        headers.push(`${baseTitle} % [${comparisonLabel}]`);
      }

      headers.push(`${baseTitle} Diff %`);
    }
  });

  // ── Build data rows ───────────────────────────────────────────────────────
  const dataRows: string[][] = primaryRows.map((row) => {
    const compRow = hasComparison
      ? comparisonRows.find((r) => r.brand_id === row.brand_id)
      : undefined;

    const cells: string[] = [];

    columns.forEach((col) => {
      const currencySymbol = ORG_CURRENCY_INCOME_KEYS.has(col.key)
        ? getCurrencySymbolFromCode(row.deal_currency)
        : getCurrencySymbol(col.key);
      const isRoi = col.key.toLowerCase() === "roi";
      const isNativePerc = NATIVE_PERCENTAGE_KEYS.has(col.key);

      const fmtCell = (v: any): string => {
        const rounded =
          (currencySymbol != null || ROUND_KEYS.has(col.key)) &&
          !isNaN(Number(v))
            ? Math.round(Number(v))
            : v;
        const str = formatValue(rounded);
        if (str === "N/A") return str;
        if (isRoi || isNativePerc) return `${str}%`;
        return currencySymbol ? `${currencySymbol}${str}` : str;
      };

      // Primary value
      if (col.isPercentageValue) {
        const raw = row[col.key];
        const pct = NATIVE_PERCENTAGE_KEYS.has(col.key)
          ? Number(raw).toFixed(1)
          : (Number(raw) * 100).toFixed(1);
        cells.push(raw != null ? `${pct}%` : "N/A");
      } else {
        cells.push(fmtCell(row[col.key]));
      }

      // Primary _perc value (combined columns only)
      if (col.hasPerc) {
        const percRaw = row[col.key + PERC_SUFFIX];
        const percStr =
          percRaw != null ? `${(Number(percRaw) * 100).toFixed(1)}%` : "N/A";
        cells.push(percStr);
      }

      if (hasComparison && !NO_COMPARISON_KEYS.has(col.key)) {
        // Comparison value
        if (col.isPercentageValue) {
          const compRaw = compRow?.[col.key];
          const pct =
            compRaw != null
              ? NATIVE_PERCENTAGE_KEYS.has(col.key)
                ? Number(compRaw).toFixed(1)
                : (Number(compRaw) * 100).toFixed(1)
              : null;
          cells.push(pct != null ? `${pct}%` : compRow ? "N/A" : "No Data");
        } else {
          cells.push(compRow ? fmtCell(compRow[col.key]) : "No Data");
        }

        // Comparison _perc value (combined columns only)
        if (col.hasPerc) {
          const compPercRaw = compRow?.[col.key + PERC_SUFFIX];
          const compPercStr =
            compPercRaw != null
              ? `${(Number(compPercRaw) * 100).toFixed(1)}%`
              : compRow
              ? "N/A"
              : "No Data";
          cells.push(compPercStr);
        }

        // Diff %
        const diff = calcDiffPercent(row[col.key], compRow?.[col.key]);
        cells.push(
          diff !== null ? `${diff >= 0 ? "+" : ""}${diff.toFixed(2)}%` : "N/A"
        );
      }
    });

    return cells;
  });

  // ── Serialize to CSV string ───────────────────────────────────────────────
  const csvLines: string[] = [
    headers.map(escapeCsvCell).join(","),
    ...dataRows.map((row) => row.map(escapeCsvCell).join(",")),
  ];
  const csvContent = csvLines.join("\n");

  // ── Trigger browser download ──────────────────────────────────────────────
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
